import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DashboardData, formatCompactCount } from '../../../services/dashboard';
import {
  bottomCardSx,
  cardBodySx,
  cardTitleSx,
  chartTooltipProps,
} from './dashboardCardStyles';

const RING_R = 92;
const STROKE = 32;
const CIRCUM = 2 * Math.PI * RING_R;
const DONUT_SIZE = 230;
const CX = DONUT_SIZE / 2;
const LABEL_MIN_PERCENT = 4;

const TYPE_ORDER = ['RETAIL', 'HNI', 'SME', 'LARGE_SCALE', 'OEM'] as const;

const normalizeKey = (key: string) => key.toUpperCase().replace(/-/g, '_');

type VisualSegment = DashboardData['overallPerformance']['segments'][number] & {
  visualPercent: number;
};

function buildVisualSegments(
  segments: DashboardData['overallPerformance']['segments'],
): VisualSegment[] {
  const active = segments.filter((s) => s.count > 0);
  const total = active.reduce((sum, s) => sum + s.count, 0);
  if (total === 0) return [];

  return active.map((s) => ({
    ...s,
    visualPercent: (s.count / total) * 100,
  }));
}

function getLabelAngle(start: number, span: number) {
  return (start + span / 2) * 3.6 - 90;
}

function polarToXY(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

type PerformanceChartProps = {
  overallPerformance: DashboardData['overallPerformance'];
};

function DonutChart({ segments }: { segments: VisualSegment[] }) {
  let acc = 0;

  if (!segments.length) {
    return (
      <svg width={DONUT_SIZE} height={DONUT_SIZE}>
        <circle r={RING_R} cx={CX} cy={CX} fill="none" stroke="#2a2a2a" strokeWidth={STROKE} />
      </svg>
    );
  }

  return (
    <svg width={DONUT_SIZE} height={DONUT_SIZE}>
      <circle r={RING_R} cx={CX} cy={CX} fill="none" stroke="#2a2a2a" strokeWidth={STROKE} />
      <g transform={`rotate(-90 ${CX} ${CX})`}>
        {segments.map((seg) => {
          const dash = (seg.visualPercent / 100) * CIRCUM;
          const gap = CIRCUM - dash;
          const offset = (acc / 100) * CIRCUM;
          acc += seg.visualPercent;
          return (
            <circle
              key={seg.key}
              r={RING_R}
              cx={CX}
              cy={CX}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset}
              style={{ transition: 'stroke-dashoffset 0.45s ease' }}
            />
          );
        })}
      </g>
    </svg>
  );
}

function ArcLabels({ segments }: { segments: VisualSegment[] }) {
  let acc = 0;
  const labelR = RING_R + 14;

  return (
    <>
      {segments.map((seg) => {
        const start = acc;
        acc += seg.visualPercent;
        if (seg.percent < LABEL_MIN_PERCENT) return null;

        const pos = polarToXY(CX, CX, labelR, getLabelAngle(start, seg.visualPercent));
        const size = seg.percent >= 20 ? 44 : 38;

        return (
          <Tooltip
            key={seg.key}
            title={
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{seg.label}</Typography>
                <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                  {seg.count.toLocaleString('en-IN')} users
                </Typography>
              </Box>
            }
            {...chartTooltipProps}
          >
            <Box
              sx={{
                position: 'absolute',
                left: pos.x - size / 2,
                top: pos.y - size / 2,
                width: size,
                height: size,
                borderRadius: '50%',
                bgcolor: '#fff',
                color: '#181818',
                fontWeight: 700,
                fontSize: seg.percent >= 20 ? 13 : 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                zIndex: 3,
                cursor: 'default',
                lineHeight: 1,
              }}
            >
              {seg.percent}%
            </Box>
          </Tooltip>
        );
      })}
    </>
  );
}

export function PerformanceChart({ overallPerformance }: PerformanceChartProps) {
  const { segments, totalCount } = overallPerformance;

  const segmentMap = useMemo(() => {
    const map = new Map<string, DashboardData['overallPerformance']['segments'][number]>();
    segments.forEach((s) => map.set(normalizeKey(s.key), s));
    return map;
  }, [segments]);

  const visualSegments = useMemo(() => buildVisualSegments(segments), [segments]);

  const legendItems = useMemo(
    () =>
      TYPE_ORDER.map((typeKey) => {
        const seg = segmentMap.get(typeKey);
        return (
          seg ?? {
            key: typeKey,
            label: typeKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            percent: 0,
            value: '0%',
            color: '#555',
            count: 0,
          }
        );
      }),
    [segmentMap],
  );

  return (
    <Box sx={{ ...bottomCardSx, py: 2.5 }}>
      <Typography
        sx={{
          ...cardTitleSx,
          textAlign: 'center',
          flexShrink: 0,
          fontSize: 21,
          fontWeight: 600,
        }}
      >
        Overall Performance
      </Typography>

      <Box sx={{ ...cardBodySx, alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
        <Box
          sx={{
            position: 'relative',
            width: DONUT_SIZE,
            height: DONUT_SIZE,
            flexShrink: 0,
            my: 0.5,
          }}
        >
          <DonutChart segments={visualSegments} />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              transform: 'translateY(-2px)',
            }}
          >
            <Typography sx={{ fontSize: 14, color: '#ccc', fontWeight: 400, mb: 0.75 }}>
              Total Count
            </Typography>
            <Typography sx={{ fontSize: 38, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
              {formatCompactCount(totalCount)}
            </Typography>
          </Box>
          <ArcLabels segments={visualSegments} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px 20px',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          px: 1,
          pt: 0.5,
        }}
      >
        {legendItems.map((seg) => (
          <Tooltip
            key={seg.key}
            title={
              seg.count > 0 ? (
                <Typography sx={{ fontSize: 12 }}>
                  {seg.count.toLocaleString('en-IN')} users · {seg.percent}%
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: '#aaa' }}>No users yet</Typography>
              )
            }
            {...chartTooltipProps}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                opacity: seg.count > 0 ? 1 : 0.45,
                cursor: 'default',
              }}
            >
              <Box
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: '50%',
                  bgcolor: seg.color,
                  flexShrink: 0,
                  border: seg.key === 'SME' ? '1px solid #555' : 'none',
                }}
              />
              <Typography sx={{ fontSize: 14, color: '#bbb', fontWeight: 400, lineHeight: 1 }}>
                {seg.label}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
